/** Enquiry form options — shared by the public form, URL pre-selection and the admin inbox. */
export const CONDITIONS = [
  'General Checkup',
  'Teeth Whitening',
  'Invisalign / Clear Aligners',
  'Dental Implants',
  'Veneers',
  'Crowns & Bridges',
  'Root Canal Treatment',
  'Tooth Extraction',
  'Dentures',
  'IV Sedation',
  'Emergency Appointment',
  'Other',
];

export const CONTACT_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

/** ?condition=<slug> (or a treatment slug from a service/area page) → dropdown label */
const SLUG_TO_CONDITION = {
  'general-checkup': 'General Checkup',
  checkup: 'General Checkup',
  'teeth-whitening': 'Teeth Whitening',
  whitening: 'Teeth Whitening',
  invisalign: 'Invisalign / Clear Aligners',
  'clear-aligners': 'Invisalign / Clear Aligners',
  'dental-implants': 'Dental Implants',
  implants: 'Dental Implants',
  'all-on-4-implants': 'Dental Implants',
  veneers: 'Veneers',
  'porcelain-veneers': 'Veneers',
  'composite-veneers': 'Veneers',
  crowns: 'Crowns & Bridges',
  bridges: 'Crowns & Bridges',
  'crowns-bridges': 'Crowns & Bridges',
  'root-canal': 'Root Canal Treatment',
  'root-canal-treatment': 'Root Canal Treatment',
  extraction: 'Tooth Extraction',
  'tooth-extraction': 'Tooth Extraction',
  dentures: 'Dentures',
  'iv-sedation': 'IV Sedation',
  sedation: 'IV Sedation',
  'dental-phobia': 'IV Sedation',
  emergency: 'Emergency Appointment',
  'emergency-dentist': 'Emergency Appointment',
  'emergency-appointment': 'Emergency Appointment',
  other: 'Other',
};

export const conditionFromSlug = (slug) => {
  if (!slug) return '';
  const s = String(slug).toLowerCase().trim();
  return SLUG_TO_CONDITION[s] || CONDITIONS.find((c) => c.toLowerCase() === s) || '';
};

/**
 * Build /enquiry?condition=<slug>&about=<label>. If the slug isn't one of the 11 dropdown
 * options, the form selects "Other" and pre-fills "I'm interested in <label>".
 */
export const enquiryPath = (slug, about) => {
  const q = new URLSearchParams();
  if (slug) q.set('condition', slug);
  if (about) q.set('about', about);
  const qs = q.toString();
  return qs ? `/enquiry?${qs}` : '/enquiry';
};

/**
 * Knowledge Base article slug → enquiry pre-fill. `slug` maps to a dropdown
 * condition (via SLUG_TO_CONDITION); where there's no matching dropdown option
 * we use slug:'other' + an `about` label so the form pre-fills a friendly message.
 */
const KB_SLUG_TO_ENQUIRY = {
  'how-long-do-dental-implants-really-last': { slug: 'implants' },
  'a-guide-to-dental-implants': { slug: 'implants' },
  'a-guide-to-bone-grafting': { slug: 'implants', about: 'dental implants and bone grafting' },
  'why-do-teeth-whitening-results-fade': { slug: 'whitening' },
  'a-guide-to-teeth-whitening': { slug: 'whitening' },
  'invisalign-vs-traditional-braces:-which-is-faster': { slug: 'invisalign' },
  'a-guide-to-braces-and-invisalign': { slug: 'invisalign' },
  'a-guide-to-composite-bonding-and-veneers': { slug: 'veneers' },
  'a-guide-to-dental-materials': { slug: 'other', about: 'crowns and veneers' },
  'a-guide-to-dental-crowns-and-onlays': { slug: 'crowns' },
  'a-guide-to-dental-bridges': { slug: 'bridges' },
  'a-guide-to-dentures': { slug: 'dentures' },
  'a-guide-to-white-fillings': { slug: 'other', about: 'a white filling' },
  'a-guide-to-gum-disease': { slug: 'other', about: 'gum disease treatment' },
  'a-guide-to-gum-recession': { slug: 'other', about: 'gum recession' },
  'a-guide-to-gum-grafting': { slug: 'other', about: 'gum grafting' },
  'a-guide-to-bad-breath-and-oral-hygiene': { slug: 'checkup' },
  'a-guide-to-tmj-and-jaw-pain': { slug: 'other', about: 'jaw pain (TMJ)' },
  'a-guide-to-acid-erosion': { slug: 'other', about: 'tooth wear from acid erosion' },
  'a-guide-to-night-guards-and-splints': { slug: 'other', about: 'a night guard for grinding' },
  'a-guide-to-modern-dental-technology': { slug: 'checkup' },
};

const CATEGORY_TO_CONDITION_SLUG = {
  Implants: 'implants',
  'Cosmetic Dentistry': 'whitening',
  'Restorative Dentistry': 'crowns',
  Orthodontics: 'invisalign',
  'Gum Health': 'other',
  'General Dentistry': 'checkup',
  'Oral Health & Prevention': 'checkup',
};

/** Resolve a KB article → { slug, about } for the Enquire CTA (pre-selects the treatment). */
export const kbEnquiryParams = (article) => {
  if (!article) return { slug: 'checkup' };
  const mapped = KB_SLUG_TO_ENQUIRY[article.slug];
  if (mapped) return mapped;
  return { slug: CATEGORY_TO_CONDITION_SLUG[article.category] || 'checkup' };
};

/** Resolve URL params → { condition, message } defaults for the form. */
export const enquiryDefaults = (params) => {
  const slug = params.get('condition');
  const about = params.get('about');
  const condition = conditionFromSlug(slug) || (about || slug ? 'Other' : '');
  const label = about || (slug && !conditionFromSlug(slug) ? slug.replace(/-/g, ' ') : '');
  const message = condition === 'Other' && label ? `I'm interested in ${label}.` : '';
  return { condition, message };
};
